import React, { useEffect } from 'react';

import { Redirect, Route, Switch } from 'react-router-dom';

import InvitationExpiredPage from 'components/InvitationExpiredPage';
import AllSetPage from 'containers/AllSetPage/Loadable';
import ProtectedRoute from 'containers/App/ProtectedRoute';
import RedirectProjectLegacyRoute from 'containers/App/RedirectProjectLegacyRoute';
import CallBackPaymentPage from 'containers/ContractPage/CallBackPaymentPage';
import PaymentPage from 'containers/ContractPage/PaymentPage';
import FormPage from 'containers/FormPage/Loadable';
import LoginCallbackPage from 'containers/LoginCallbackPage';
import LoginPage from 'containers/LoginPage';
import OneProjectPage from 'containers/OneProjectPage/Loadable';
import OneProjectPlanPage from 'containers/OneProjectPlanPage/Loadable';
import OrganizationPage from 'containers/OrganizationPage/Loadable';
import Page403 from 'containers/Page403/Loadable';
import Page404 from 'containers/Page404/Loadable';
import Page404Project from 'containers/Page404Project/Loadable';
import Projects from 'containers/Projects/Loadable';
import RootRedirect from 'containers/RootRedirect';
import SuperAdminPage from 'containers/SuperAdmin/Loadable';
import WorkspacePage from 'containers/WorkspacePage';
import { appPaths, pathFallback } from 'utils/constants';
import initialLoader from 'utils/initialLoader';

const createRedirectIfNeeded = (fromPath, toPath, exact) => {
  if (fromPath !== toPath) {
    return <Redirect key={`${fromPath}-redirect`} exact={exact} from={fromPath} to={toPath} />;
  }
  return null;
};

const projectRouteKey = 'project-route';

const createRoutesWithFallback = (path, component, exact = false, routeKey = path) => {
  const routes = [
    <ProtectedRoute key={routeKey} exact={exact} path={path} component={component} />,
  ];

  let replacedPath = path;
  pathFallback.forEach(([search, replace]) => {
    replacedPath = replacedPath.replace(new RegExp(replace, 'g'), search);
  });
  const redirect = createRedirectIfNeeded(replacedPath, path, exact);
  if (redirect) routes.push(redirect);

  pathFallback.forEach(([search, replace]) => {
    const newPath = path.replace(new RegExp(replace, 'g'), search);
    const redirectRoute = createRedirectIfNeeded(newPath, path, exact);
    if (redirectRoute) routes.push(redirectRoute);
  });

  return routes;
};

const AppWithRouterAccess = () => {
  useEffect(() => {
    initialLoader.hideLoader();
  }, []);

  return (
    <Switch>
      <Route exact path="/" component={RootRedirect} />
      <Route exact path="/login" component={LoginPage} />
      <Route exact path="/login/callback" component={LoginCallbackPage} />
      <Route exact path="/invitation/expired" component={InvitationExpiredPage} />
      <ProtectedRoute exact path="/welcome" component={AllSetPage} />
      {createRoutesWithFallback('/organization/:organizationId/projects', Projects, true).map(
        route => route,
      )}
      <Route path="/projects/:idData" component={RedirectProjectLegacyRoute} />
      <Route path="/projects" component={RedirectProjectLegacyRoute} />
      {createRoutesWithFallback(
        '/organization/:organizationId/workspace/:workspaceId/form/:formId',
        FormPage,
        true,
      ).map(route => route)}
      {createRoutesWithFallback('/organization/:organizationId/form/:formId', FormPage).map(
        route => route,
      )}
      {createRoutesWithFallback(
        '/organization/:organizationId/workspace/:workspaceId/:pageName?/:itemId?/:subItemId?',
        WorkspacePage,
      ).map(route => route)}
      {createRoutesWithFallback(
        '/organization/:organizationId/project/:idData/plans/folder/:itemId/plan/:planId/:planType/:associatedPlanId?',
        OneProjectPlanPage,
        false,
        projectRouteKey,
      ).map(route => route)}
      {createRoutesWithFallback(appPaths.projectPage, OneProjectPage, true, projectRouteKey).map(
        route => route,
      )}
      {createRoutesWithFallback('/organization/:organizationId/form/:formId', FormPage).map(
        route => route,
      )}
      {createRoutesWithFallback(
        '/organization/:organizationId/:pageName?/:itemId?/:subItemId?',
        OrganizationPage,
        true,
      ).map(route => route)}
      {createRoutesWithFallback('/form/:formId', FormPage).map(route => route)}
      <ProtectedRoute
        exact
        path="/super-admin/:pageName/:libraryName?/:itemId?"
        component={SuperAdminPage}
      />
      <Route path="/403" component={Page403} />
      <Route path="/organizations/:organizationId/404" component={Page404Project} />
      <Route path="/404" component={Page404} />
      <Route path="/checkout" component={PaymentPage} />
      <Route path="/return" component={CallBackPaymentPage} />
      <Route component={Page404} />
    </Switch>
  );
};

export default AppWithRouterAccess;
